import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Save, Moon, Sun, Monitor, Bell, Mail, Smartphone, Languages, ShieldCheck, KeyRound, ScrollText, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import tr from "@/i18n/tr";
import { useTheme } from "@/context/ThemeContext";
import api from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { useCan } from "@/components/common/Can";

function ThemeOption({ value, current, onSelect, Icon, label }) {
    const active = value === current;
    return (
        <button
            type="button"
            onClick={() => onSelect(value)}
            className={`flex flex-col items-center gap-2 rounded-2xl border p-4 transition-all ${
                active
                    ? "border-[hsl(var(--brand))] bg-[hsl(var(--brand))]/5"
                    : "border-border hover:border-[hsl(var(--brand))]/40"
            }`}
            data-testid={`theme-option-${value}"`}
        >
            <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${active ? "bg-[hsl(var(--brand))] text-white" : "bg-muted text-foreground"}`}>
                <Icon className="h-5 w-5" />
            </div>
            <span className="text-[12.5px] font-semibold">{label}</span>
        </button>
    );
}

function ToggleRow({ Icon, title, desc, checked, onCheckedChange, testId }) {
    return (
        <div className="flex items-center justify-between rounded-2xl border border-border bg-background p-4">
            <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-muted text-foreground">
                    <Icon className="h-5 w-5" />
                </div>
                <div>
                    <div className="text-[13.5px] font-semibold">{title}</div>
                    <div className="text-[11.5px] text-muted-foreground">{desc}</div>
                </div>
            </div>
            <Switch checked={checked} onCheckedChange={onCheckedChange} data-testid={testId} />
        </div>
    );
}

export default function SettingsPage() {
    const { theme, setTheme } = useTheme();
    const canManageRoles = useCan("roles.view");
    const canViewPermissions = useCan("permissions.view");
    const canViewAudit = useCan("audit.view");
    const [settings, setSettings] = useState({
        theme: theme,
        language: "tr",
        notifications_email: true,
        notifications_push: true,
        notifications_desktop: false,
        timezone: "Europe/Istanbul",
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/users/me/settings");
                setSettings((s) => ({ ...s, ...data, theme }));
            } catch (_) {
                // ignore
            }
        })();
    }, [theme]);

    const handleThemeSelect = (t) => {
        setSettings({ ...settings, theme: t });
        setTheme(t);
    };

    const onSave = async () => {
        setSaving(true);
        try {
            await api.put("/users/me/settings", settings);
            toast.success(tr.settings.saved);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Kaydedilemedi.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-[26px] font-bold tracking-tight">{tr.settings.title}</h1>
                    <p className="text-[13.5px] text-muted-foreground mt-1">{tr.settings.subtitle}</p>
                </div>
                <button onClick={onSave} disabled={saving} className="btn-brand h-11" data-testid="settings-save">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    {tr.common.save}
                </button>
            </div>

            {/* Appearance */}
            <section className="card-elevated p-6">
                <div className="mb-5">
                    <div className="text-[15px] font-semibold">{tr.settings.appearance}</div>
                    <div className="text-[12.5px] text-muted-foreground mt-0.5">{tr.settings.appearanceDesc}</div>
                </div>
                <div className="grid grid-cols-3 gap-3 max-w-md">
                    <ThemeOption value="light" current={settings.theme} onSelect={handleThemeSelect} Icon={Sun}     label={tr.common.light} />
                    <ThemeOption value="dark"  current={settings.theme} onSelect={handleThemeSelect} Icon={Moon}    label={tr.common.dark} />
                    <ThemeOption value="system" current={settings.theme} onSelect={handleThemeSelect} Icon={Monitor} label={tr.common.system} />
                </div>
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
                    <div className="space-y-1.5">
                        <label className="flex items-center gap-2 text-[12.5px] font-semibold">
                            <Languages className="h-4 w-4" /> {tr.common.language}
                        </label>
                        <select
                            value={settings.language}
                            onChange={(e) => setSettings({ ...settings, language: e.target.value })}
                            className="h-11 w-full rounded-xl border border-border bg-background px-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand/40"
                            data-testid="settings-language"
                        >
                            <option value="tr">{tr.common.turkish}</option>
                        </select>
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-[12.5px] font-semibold">{tr.settings.timezone}</label>
                        <select
                            value={settings.timezone}
                            onChange={(e) => setSettings({ ...settings, timezone: e.target.value })}
                            className="h-11 w-full rounded-xl border border-border bg-background px-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand/40"
                            data-testid="settings-timezone"
                        >
                            <option value="Europe/Istanbul">Europe/Istanbul (GMT+3)</option>
                            <option value="Europe/London">Europe/London (GMT+0)</option>
                            <option value="America/New_York">America/New_York (GMT-5)</option>
                        </select>
                    </div>
                </div>
            </section>

            {/* Notifications */}
            <section className="card-elevated p-6">
                <div className="mb-5">
                    <div className="text-[15px] font-semibold">{tr.settings.notifications}</div>
                    <div className="text-[12.5px] text-muted-foreground mt-0.5">{tr.settings.notificationsDesc}</div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <ToggleRow
                        Icon={Mail}
                        title={tr.settings.emailNotifications}
                        desc="Kritik olayları e-posta ile al"
                        checked={settings.notifications_email}
                        onCheckedChange={(v) => setSettings({ ...settings, notifications_email: v })}
                        testId="toggle-email"
                    />
                    <ToggleRow
                        Icon={Bell}
                        title={tr.settings.pushNotifications}
                        desc="Uygulama içi anlık bildirimler"
                        checked={settings.notifications_push}
                        onCheckedChange={(v) => setSettings({ ...settings, notifications_push: v })}
                        testId="toggle-push"
                    />
                    <ToggleRow
                        Icon={Smartphone}
                        title={tr.settings.desktopNotifications}
                        desc="Tarayıcı masaüstü bildirimleri"
                        checked={settings.notifications_desktop}
                        onCheckedChange={(v) => setSettings({ ...settings, notifications_desktop: v })}
                        testId="toggle-desktop"
                    />
                </div>
            </section>

            {/* Management (Phase 02) */}
            {(canManageRoles || canViewPermissions || canViewAudit) && (
                <section className="card-elevated p-6">
                    <div className="mb-5">
                        <div className="text-[15px] font-semibold">{tr.settings.management}</div>
                        <div className="text-[12.5px] text-muted-foreground mt-0.5">{tr.settings.managementDesc}</div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {canManageRoles && (
                            <Link to="/roles" className="flex items-center justify-between rounded-2xl border border-border bg-background p-4 hover:border-[hsl(var(--brand))]/40 hover:shadow-card-hover transition-all" data-testid="settings-open-roles">
                                <div className="flex items-start gap-3 min-w-0">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[hsl(var(--brand))]/10 text-[hsl(var(--brand))]">
                                        <ShieldCheck className="h-5 w-5" />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="text-[13.5px] font-semibold">{tr.settings.openRoles}</div>
                                        <div className="text-[11.5px] text-muted-foreground">Rol yetkilerini yönet</div>
                                    </div>
                                </div>
                                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                            </Link>
                        )}
                        {canViewPermissions && (
                            <Link to="/permissions" className="flex items-center justify-between rounded-2xl border border-border bg-background p-4 hover:border-[hsl(var(--brand))]/40 hover:shadow-card-hover transition-all" data-testid="settings-open-permissions">
                                <div className="flex items-start gap-3 min-w-0">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[hsl(var(--info))]/10 text-[hsl(var(--info))]">
                                        <KeyRound className="h-5 w-5" />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="text-[13.5px] font-semibold">{tr.settings.openPermissions}</div>
                                        <div className="text-[11.5px] text-muted-foreground">Yetki kataloğunu görüntüle</div>
                                    </div>
                                </div>
                                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                            </Link>
                        )}
                        {canViewAudit && (
                            <Link to="/audit-log" className="flex items-center justify-between rounded-2xl border border-border bg-background p-4 hover:border-[hsl(var(--brand))]/40 hover:shadow-card-hover transition-all" data-testid="settings-open-audit">
                                <div className="flex items-start gap-3 min-w-0">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]">
                                        <ScrollText className="h-5 w-5" />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="text-[13.5px] font-semibold">{tr.settings.openAudit}</div>
                                        <div className="text-[11.5px] text-muted-foreground">Sistem denetim izleri</div>
                                    </div>
                                </div>
                                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                            </Link>
                        )}
                    </div>
                </section>
            )}
        </div>
    );
}
